import React, { useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync } from "@fortawesome/free-solid-svg-icons"
import { COLORS } from "../../utilities/Constants"
import { generalStyles } from "../styles/styles"
import { FieldColumn } from "../cards/EntiteCards"
import CustomModal from "../basics/interface/CustomModal"
import { HTtoTTC, round100 } from "../../utilities/Addons"
import InputV2 from "../basics/input/InputV2"
import { PdfTile } from "../basics/interface/AppPdfs"
import { URL, handleErrors } from "../../store/actions/depannageActions"

const IMG_EXT = ["jpg", "jpeg", "png", "gif", "webp", "bmp"]

// URL absolue propre (évite le double slash entre URL et file.url).
const absUrl = (url) => {
	if (!url) return ""
	const base = URL.endsWith("/") ? URL.slice(0, -1) : URL
	return `${base}${url.startsWith("/") ? url : `/${url}`}`
}

// Version V2 (design system AtelierV2) de AtelierSousTraitant : carte récap par
// sous-traitant avec totaux recalculés (lecture seule), date « Payé le » et
// justificatif (image ou PDF). Upload/suppression immédiats sur api/sous_traitants
// (fichier binaire, hors POST atelier JSON) ; l'affichage dérive du prop `file`.
const AtelierSousTraitantV2 = ({
	sousTraitant,
	updateSousTraitant,
	index,
	tva,
	// Fiche close ou ouverte ailleurs : date « Payé le » + justificatif verrouillés.
	locked = false,
}) => {
	const { token } = useSelector((state) => state.navInfo.login)
	const fileInputRef = useRef(null)
	const [uploading, setUploading] = useState(false)
	const [confirmDelete, setConfirmDelete] = useState(false)
	const [deleting, setDeleting] = useState(false)
	const [tileHover, setTileHover] = useState(false)
	const {
		id,
		label,
		totalMainOeuvres,
		totalFournitures,
		totalAutres,
		payeLe,
	} = sousTraitant

	// Source d'affichage du justificatif : état local, mis à jour optimistiquement
	// à l'upload/suppression (réponse serveur) et resynchronisé si le prop change.
	// Évite de dépendre uniquement de la propagation via le form parent.
	const [file, setFile] = useState(sousTraitant.file)
	useEffect(() => {
		setFile(sousTraitant.file)
	}, [sousTraitant.file])

	const totalHT = useMemo(() => {
		const parts = [totalMainOeuvres, totalFournitures, totalAutres].map(
			parseFloat,
		)
		if (parts.some((n) => Number.isNaN(n))) return 0
		return parts.reduce((a, b) => a + b, 0)
	}, [totalMainOeuvres, totalFournitures, totalAutres])
	const totalTTC = useMemo(
		() => round100(HTtoTTC(totalHT, tva)),
		[totalHT, tva],
	)

	const hasFile = !!(file && file.saved && file.url)
	const fileName = hasFile ? file.url.split("/").pop() : ""
	const isImage =
		hasFile && IMG_EXT.includes(fileName.split(".").pop().toLowerCase())

	const onPickFile = async (e) => {
		const picked = e.target.files[0]
		e.target.value = ""
		if (!picked || !id) return
		setUploading(true)
		const formData = new FormData()
		formData.append("id", id)
		formData.append("file", picked)
		try {
			const json = await fetch(`${URL}api/sous_traitants`, {
				method: "POST",
				headers: { Authorization: `Bearer ${token}` },
				body: formData,
			})
				.then(handleErrors)
				.then((res) => res.json())
			setFile(json.file)
			updateSousTraitant("file", json.file, index)
		} finally {
			setUploading(false)
		}
	}

	const confirmDeleteFile = async () => {
		if (!id) return
		setDeleting(true)
		const formData = new FormData()
		formData.append("id", id)
		try {
			await fetch(`${URL}api/sous_traitants`, {
				method: "DELETE",
				headers: { Authorization: `Bearer ${token}` },
				body: formData,
			}).then(handleErrors)
			setFile({ saved: false, url: "" })
			updateSousTraitant("file", { saved: false, url: "" }, index)
			setConfirmDelete(false)
		} finally {
			setDeleting(false)
		}
	}

	const renderJustificatif = () => {
		if (!id)
			return (
				<span style={{ fontSize: 12, color: COLORS.textLight }}>
					Enregistrez la fiche pour joindre un justificatif.
				</span>
			)
		if (uploading)
			return (
				<div style={tileStyle}>
					<FontAwesomeIcon
						icon={faSync}
						spin
						style={{ color: COLORS.textLight }}
					/>
				</div>
			)
		// Tuile « Ajouter » (aucun justificatif) — ou placeholder verrouillé.
		if (!hasFile)
			return locked ? (
				<div
					style={{
						...tileStyle,
						border: `1px dashed ${COLORS.border}`,
						color: COLORS.textLight,
						fontSize: 18,
					}}
				>
					—
				</div>
			) : (
				<div
					onClick={() => fileInputRef.current?.click()}
					title="Ajouter un justificatif"
					style={{
						...tileStyle,
						border: `1px dashed ${COLORS.borderStrong}`,
						cursor: "pointer",
						color: COLORS.textLight,
						fontSize: 22,
					}}
				>
					+
				</div>
			)
		if (!isImage)
			return (
				<PdfTile
					doc={{ url: absUrl(file.url), name: fileName }}
					onOpen={() => window.open(absUrl(file.url))}
					onDelete={locked ? undefined : () => setConfirmDelete(true)}
					readOnly={locked}
					size={tileStyle.width}
					height={tileStyle.height}
				/>
			)
		return (
			<div
				onMouseEnter={() => setTileHover(true)}
				onMouseLeave={() => setTileHover(false)}
				onClick={() => window.open(absUrl(file.url))}
				title={fileName}
				style={{
					...tileStyle,
					position: "relative",
					cursor: "pointer",
				}}
			>
				<img
					src={absUrl(file.url)}
					alt=""
					style={{
						width: "100%",
						height: "100%",
						objectFit: "cover",
						display: "block",
					}}
				/>
				{tileHover && !locked && (
					<button
						type="button"
						title="Supprimer"
						onClick={(e) => {
							e.stopPropagation()
							setConfirmDelete(true)
						}}
						style={{
							position: "absolute",
							top: 0,
							right: 0,
							width: 26,
							height: 26,
							borderRadius: "50%",
							border: "none",
							backgroundColor: COLORS.danger,
							color: "#fff",
							fontSize: 16,
							fontWeight: 300,
							lineHeight: 1,
							cursor: "pointer",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						×
					</button>
				)}
				<div
					style={{
						position: "absolute",
						bottom: 0,
						left: 0,
						right: 0,
						padding: "2px 5px",
						backgroundColor: "rgba(28,25,23,0.6)",
						fontSize: 9,
						color: "#fff",
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					{fileName}
				</div>
			</div>
		)
	}

	return (
		<div
			style={{
				backgroundColor: COLORS.surface,
				border: `1px solid ${COLORS.border}`,
				borderRadius: 14,
				overflow: "hidden",
				marginTop: index === 0 ? 0 : 20,
			}}
		>
			{confirmDelete && (
				<CustomModal
					title="Supprimer le justificatif ?"
					onClose={() => !deleting && setConfirmDelete(false)}
					showCloseButton={!deleting}
					showFooter={!deleting}
					buttons={[
						{
							title: "Annuler",
							styleName: "secondary",
							onClick: () => setConfirmDelete(false),
						},
						{
							title: "Supprimer",
							styleName: "danger",
							onClick: confirmDeleteFile,
						},
					]}
				>
					{deleting ? (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								padding: "12px 0",
							}}
						>
							<FontAwesomeIcon icon={faSync} spin size="2x" />
						</div>
					) : (
						<p style={{ margin: 0 }}>
							Le justificatif sera définitivement supprimé.
						</p>
					)}
				</CustomModal>
			)}

			{/* Header : nom du sous-traitant */}
			<div
				style={{
					padding: "10px 16px",
					backgroundColor: COLORS.bg,
					borderBottom: `1px solid ${COLORS.border}`,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<span
					style={{
						fontSize: 13,
						fontWeight: 600,
						color: COLORS.text,
					}}
				>
					{label}
				</span>
				<span
					style={{
						...generalStyles.badge(
							COLORS.danger,
							COLORS.dangerLight,
						),
					}}
				>
					{totalTTC} CHF TTC
				</span>
			</div>

			{/* Corps */}
			<div
				style={{
					padding: 16,
					display: "flex",
					flexDirection: "column",
					gap: 14,
				}}
			>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(5, 1fr)",
						gap: 12,
					}}
				>
					<FieldColumn label="Main d'œuvre">
						<input
							value={round100(totalMainOeuvres)}
							readOnly
							style={generalStyles.inputReadonly}
						/>
					</FieldColumn>
					<FieldColumn label="Fournitures">
						<input
							value={round100(totalFournitures)}
							readOnly
							style={generalStyles.inputReadonly}
						/>
					</FieldColumn>
					<FieldColumn label="Autres">
						<input
							value={round100(totalAutres)}
							readOnly
							style={generalStyles.inputReadonly}
						/>
					</FieldColumn>
					<FieldColumn label="Total HT">
						<input
							value={round100(totalHT)}
							readOnly
							style={generalStyles.inputReadonly}
						/>
					</FieldColumn>
					<FieldColumn label="Total TTC">
						<input
							value={totalTTC}
							readOnly
							style={{
								...generalStyles.inputReadonly,
								color: COLORS.danger,
								fontWeight: 600,
							}}
						/>
					</FieldColumn>
				</div>

				<div
					style={{
						display: "flex",
						alignItems: "flex-end",
						gap: 24,
					}}
				>
					<FieldColumn label="Payé le" style={{ width: 200 }}>
						<InputV2
							type="date"
							value={payeLe || ""}
							onChange={(e) =>
								updateSousTraitant(
									"payeLe",
									e.target.value,
									index,
								)
							}
							disabled={locked}
							style={generalStyles.input}
						/>
					</FieldColumn>
					<FieldColumn label="Justificatif">
						{renderJustificatif()}
						<input
							ref={fileInputRef}
							type="file"
							accept="image/*,application/pdf"
							onChange={onPickFile}
							style={{ display: "none" }}
						/>
					</FieldColumn>
				</div>
			</div>
		</div>
	)
}

// Dimension commune des tuiles justificatif (vignette / "+" / en attente).
const tileStyle = {
	width: 76,
	height: 80,
	borderRadius: 10,
	border: `1px solid ${COLORS.border}`,
	backgroundColor: COLORS.bg,
	overflow: "hidden",
	flexShrink: 0,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
}

export default AtelierSousTraitantV2
